// import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Toolbar, Item } from 'devextreme-react/toolbar';
import { DataGrid } from 'devextreme-react';
import { Editing, Texts, Column, Scrolling } from 'devextreme-react/data-grid';
import { loader } from 'graphql.macro';
import { gqlClient } from 'gql-client';
import { useDate } from 'hooks';
import { CustomRule } from 'devextreme-react/validator';
import { custom } from 'devextreme/ui/dialog';
import { showWarning } from 'utils/notify';
import dayjs from 'dayjs';
import { useAuth } from 'contexts';
import { WorktimeDoc, WorktimeRow } from './worktimeTypes';
import { fetchGraphQLData } from 'utils/gql-datasource';
const getWorkersFrom1C = loader('./gql/getWorkersFrom1C.gql');

interface WorkersGridProps {
  data: WorktimeDoc,
  gData: any,
  legend: any[],
}

interface Worker1C {
  person_id: string;
  hours: {
    day: number;
    hoursgraph: number;
  }[];
}

export const WorkersGrid = (props:WorkersGridProps) => {

  const [ data, setData ] = useState<Array<WorktimeRow>>([]);
  const [ dataGraph, setDataGraph ] = useState(new Map());
  const [ docReadOnly ] = useState(false);
  const { options, lab } = useAuth();
  const { formatDate, startOfMonth } = useDate();
  const [ protectDate, setProtectDate ] = useState(startOfMonth());

  const servicesDataGridRef = useRef<DataGrid>(null);
  const focusedRowKey = useRef(null);

  const legend = props.legend;
  props.gData.current = data;

  const updateData = (newData: WorktimeRow[]) => {
    setData(newData);
    props.gData.current = newData;
  };

  const fillValidatorGraph = useCallback(() => {
    gqlClient.query({
      query: getWorkersFrom1C,
      variables: { period_reg: formatDate(props.data.period_reg) },
    }).then((resp) => {
      const workers = ( resp?.data?.worktime?.workers || []) as Array<Worker1C>;
      if (workers.length) {
        const workerGraphMap = new Map();
        workers.forEach((wrow) => {
          const hoursGraphMap = new Map();
          wrow.hours.forEach((hrow) => hoursGraphMap.set(`d_${hrow.day}`, hrow.hoursgraph));
          workerGraphMap.set(wrow.person_id, hoursGraphMap);
        });
        setDataGraph(workerGraphMap);
      }
    },
    );
  }, [ formatDate, props.data.period_reg ]);

  useEffect(() => {
    if (lab) {
      const gridOptions = options.closeByDocs?.grid.find((r) => r.docType === 'doc.work_time');
      const date = gridOptions?.byOrgs.find((r) => r.organization === lab.organization)?.date;
      if (date && date > startOfMonth(props.data.period_reg)) {
        setProtectDate(date);
      } else {
        setProtectDate(startOfMonth());
      }
    }
  }, [ lab, options, props.data.period_reg, startOfMonth ]);

  useEffect(() => {
    const worktable:Array<WorktimeRow> = [];
    const days = dayjs(props.data.period_reg).endOf('month').date();
    props.data?.worktable?.forEach((row) => {
      const obj = { ...row };
      if (row?.hours?.length > 0) {
        obj.hours = row.hours.filter((r) => r.day <= days);
        obj.hours.forEach((hrow) => { obj[`d_${hrow.day}`] = hrow.hours; });
      }
      worktable.push(obj);
    });
    if (worktable.length) fillValidatorGraph();
    setData(worktable);
  }, [ props.data, fillValidatorGraph ]);

  const updateWorkerData = (existingData: WorktimeRow[], newWorkers: WorktimeRow[]) => {
    // Filter out new workers not already in the existing data
    const filteredNewWorkers = newWorkers.filter(
      (newWorker) => !existingData.find((existing) => existing.person_id === newWorker.person_id)
    );

    // Update existing workers with new data
    const updatedData = existingData.map((existing) => {
      const updatedWorker = newWorkers.find((newWorker) => newWorker.person_id === existing.person_id);
      if (updatedWorker) {
        return {
          ...existing,
          graphic_id: updatedWorker.graphic_id,
          person_fio: updatedWorker.person_fio,
          graphic_name: updatedWorker.graphic_name,
          positions: updatedWorker.positions,
        };
      }
      return existing;
    });

    // Merge updated data with new workers
    return [ ...updatedData, ...filteredNewWorkers ];
  };

  const fillGrid = async(e: any) => {

    const _data = await fetchGraphQLData(getWorkersFrom1C, { period_reg: formatDate(props.data.period_reg) });
    if (!_data) return;

    const workers: WorktimeRow[] = _data?.worktime?.workers || [];

    if (workers.length) {
      // Add day-specific hours to each worker
      workers.forEach((worker) => {
        worker.hours.forEach((hour) => { worker[`d_${hour.day}`] = hour.hours; });
      });

      if (e?.element.id === 'refresh') {
        const updatedData = updateWorkerData(data, workers);
        setData(updatedData);
        props.gData.current = updatedData;
        return;
      }
    } else {
      showWarning('Відсутні прийняті в лабораторію працівники');
    }

    updateData(workers);
    fillValidatorGraph();
  };

  const fillButtonOptions = {
    text: 'Заповнити',
    icon: 'download',
    onClick: (e: any) => {
      if (data.length) {
        const myDialog = custom({
          title: 'Таблиця заповнена.',
          messageHtml: `
            <p style="text-align:center;">
            <b>Заповнення перезапише усі дані<br/>
            Продовжити?
            </b></p>`,
          buttons: [
            { text: 'Так', onClick: () => 1 },
            { text: 'Ні', onClick: () => 2 },
          ],
        });
        myDialog.show().then((dialogResult:number) => {
          if (dialogResult === 1) fillGrid(e);
        });
      } else {
        fillGrid(e);
      }
    },
    hint: 'Заповнити за даними обліку',
  };
  const refreshButtonOptions = {
    text: 'Обновити',
    elementAttr: { id: 'refresh' },
    icon: 'import',
    onClick: (e: any) => fillGrid(e),
    hint: 'Оновити перелік працівників',
  };

  const isSunday = useCallback((day: number) => dayjs(props.data.period_reg).startOf('month').add(day - 1, 'day').day() === 0, [ props.data.period_reg ]);

  const dayCellHeader = useCallback((day: number) => <div style={{ color: isSunday(day) ? 'red' : 'inherit' }}>{day}</div>, [ isSunday ]);

  interface validatorHoursProps {
    data: { person_id: any; };
    column: { dataField: any; };
    rule: any;
    value: any;
  }
  const validatorHours = useCallback((e:validatorHoursProps) => {
    const {
      data: { person_id },
      column: { dataField },
      rule,
      value,
    } = e;
    const personLimit = dataGraph.get(person_id)?.get(dataField) || 0;
    const limit = personLimit;
    rule.message = `Максимум-мінімум: ${limit}`;
    return ((limit === Number(value)) || legend.find((r) => r.value === value));
  }, [ dataGraph, legend ]);


  const renderHoursColumns = (data: WorktimeRow[], protectDate: string, props: WorkersGridProps) => data[0]
    ?.hours?.map((rec) => (
      <Column
        key={rec.day}
        alignment="center"
        dataField={`d_${rec.day}`}
        caption={rec.day}
        allowEditing={
          !isSunday(rec.day) &&
          rec.day >= dayjs(protectDate).get('date') &&
          (dayjs().get('date') >= rec.day || startOfMonth() > startOfMonth(props.data.period_reg))
        }
        allowSorting={false}
        headerCellRender={() => dayCellHeader(rec.day)}
        width={50}
        dataType="string"
      >
        <CustomRule reevaluate={true} ignoreEmptyValue={true} validationCallback={validatorHours}/>
      </Column>
    ));

  // useTraceUpdate({startOfMonth, formatDate})
  return (
    <>
      <Toolbar className='otk-dg-toolbar-border dx-theme-border-color'>
        <Item
          location='before'
          locateInMenu='auto'
          widget='dxButton'
          disabled={docReadOnly || data.length}
          options={fillButtonOptions}
        />
        <Item
          location='before'
          locateInMenu='auto'
          widget='dxButton'
          disabled={docReadOnly || !data.length}
          options={refreshButtonOptions}
        />
      </Toolbar>
      <DataGrid
        ref={servicesDataGridRef}
        noDataText='Список порожній'
        remoteOperations={false}
        rowAlternationEnabled
        showBorders
        showColumnLines
        allowColumnResizing
        hoverStateEnabled
        columnResizingMode='widget'
        columnAutoWidth
        keyExpr={'person_id'}
        dataSource={data}
        focusedRowEnabled={!docReadOnly}
        errorRowEnabled={false}
        onCellPrepared={(e) => {
          if (e.column?.name?.startsWith('d_') && !e.column.allowEditing) {
            e.cellElement.style.background = 'lightgrey';
          }
        }}
        onContextMenuPreparing={(e) => {
          if (e.column?.name?.startsWith('d_')) {
            if (e.row?.rowType === 'header' || !e.column.allowEditing) {
              e.items = [];
              return;
            }
            e.items = legend.map((r) => (
              { text: `${r.value} - ${r.caption}`,
                onItemClick: () => {
                  if (e.row?.data && e.column?.name) e.row.data[e.column.name] = r.value;
                  servicesDataGridRef.current?.instance.refresh(true);
                },
              }));
          }
        }}
        // onRowUpdating={handleRowUpdating}
        onFocusedRowKeyChange={(rowKey) => { focusedRowKey.current = rowKey; }}>
        <Editing
          mode='cell'
          allowAdding={false}
          allowUpdating={true}
          allowDeleting={false}
          useIcons={true}
          confirmDelete={true}>
          <Texts confirmDeleteMessage='Вилучити?' deleteRow='вилучити' />
        </Editing>
        <Scrolling showScrollbar='always' useNative={true}/>

        <Column dataField='person_fio' caption='Працівник' allowEditing={false}/>
        <Column dataField='graphic_name' caption='Графік' allowEditing={false} width={230}/>
        <Column dataField='positions' caption='Ставок' width={65} allowEditing={false}/>
        {data.length > 0 && renderHoursColumns(data, protectDate, props)}

      </DataGrid>
    </>
  );
};


